import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { CheckboxInput } from "../../components/CheckboxInput/CheckboxInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { Request, RequestType } from "../../models/Request";
import { Product, ProductType } from "../../models/Product";
import { Partner } from "../../models/Partner";
import { Warehouse } from "../../models/Warehouse";
import { City } from "../../models/City";
import { RoleType } from "../../models/Role";
import { activeUser } from "../../services/AuthService";
import {
  getAllCities,
  createCity,
  updateOneCity,
} from "../../services/CityService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllProducts } from "../../services/ProductService";
import { getAllWarehouses } from "../../services/WarehouseService";
import {
  getOneRequest,
  addNewRequest,
  updateOneRequest,
  getAllDocuments,
  getAllRequests,
  getReproAvailableSales,
  getOnHand,
} from "../../services/NewVersionService";
import { getVirtualReproWarehouse } from "../../services/VirtualWarehouseService";
import useWindowDimensions from "../../hooks/DimensionsHook";
import "./NewRepromaterialRequestForm.scss";

export interface NewRepromaterialRequestFormProps {
  show?: string;
  request?: Request;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonStyle?: React.CSSProperties;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewCityStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
  refreshingData?: () => void;
}

export const NewRepromaterialRequestForm = (
  p: NewRepromaterialRequestFormProps,
) => {
  const {
    request,
    show,
    buttonLabel,
    buttonIcon,
    buttonStyle,
    buttonSaveStyle,
    buttonNewCityStyle,
    buttonResetStyle,
    refreshingData,
  } = p;
  const { t } = useTranslation();

  const { isMobile } = useWindowDimensions();

  const [
    visibleNewRepromaterialRequestForm,
    setVisibleNewRepromaterialRequestForm,
  ] = useState<boolean>(false);

  const [products, setProducts] = useState<Product[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);

  const [newRepromaterialRequest, setNewRepromaterialRequest] =
    useState<Request>({
      id: request?.id ?? "",
      type: request?.type ?? RequestType.Import,
      product:
        request?.product ??
        ({
          id: "",
          type: ProductType.RawMaterial,
          code: "",
          name: "",
          amount: "",
          category: {
            id: "",
            type: ProductType.RawMaterial,
            name: "",
          },
          note: "",
        } as Product),
      repromaterialRequestProducts: request?.repromaterialRequestProducts ?? [],
      repromaterialRequestProductsJSON:
        request?.repromaterialRequestProductsJSON ?? "",
      year: request?.year ?? new Date().getFullYear(),
      partner: request?.partner ?? ({} as Partner),
      place: request?.place ?? "",
      inputCity: request?.inputCity ?? undefined,
      inputWarehouse: request?.inputWarehouse ?? undefined,
      amount: request?.amount ?? 0,
      date: new Date(request?.date as Date) ?? undefined,
      transportCost: request?.transportCost ?? false,
      commercialistApproved: request?.commercialistApproved ?? false,
      backofficeApproved: request?.backofficeApproved ?? false,
      contact: request?.contact ?? "",
      note: request?.note ?? "",
    });

  const [newProduct, setNewProduct] = useState<Product>({
    id: "",
    name: "",
    type: ProductType.RawMaterial,
    code: "",
    lot: "",
    amount: "",
    measuringUnit: "",
    note: "",
  });
  const [productsDataTable, setProductsDataTable] = useState<
    {
      id: string;
      name: string;
      amount: string;
      measuringUnit: string;
      note: string;
    }[]
  >(() => {
    if (
      newRepromaterialRequest.repromaterialRequestProducts &&
      products.length > 0
    ) {
      return newRepromaterialRequest.repromaterialRequestProducts.map(
        (product) => ({
          id: product.id,
          name: product.name,
          amount: product.amount ? product.amount.toString() : "0",
          measuringUnit: product.measuringUnit || "",
          note: product.note || "",
        }),
      );
    }
    return [];
  });
  const [maxAmount, setMaxAmount] = useState<number>(0); //maksimalno dostupna kolicina za selektovani magacin i selektovanu robu
  const [maxVirtualAmount, setMaxVirtualAmount] = useState<number>(0); //maksimalno dostupna kolicina za selektovanu robu u virtuelnom magacinu
  const [virtualStock, setVirtualStock] = useState<any[]>([]);
  const [visibleNewCityAddForm, setVisibleNewCityAddForm] =
    useState<boolean>(false);
  const [isLoadingPartners, setIsLoadingPartners] = useState<boolean>(true);
  const [newCity, setNewCity] = useState<City>({
    code: "",
    name: "",
    id: "",
  });
  const [repromaterialInputsApi, setRepromaterialInputsApi] = useState<any[]>(
    [],
  );
  const [repromaterialOutputsApi, setRepromaterialOutputsApi] = useState<any[]>(
    [],
  );
  const [reproRequestsApi, setReproRequestsApi] = useState<any[]>([]);

  const [maxAmountNew, setMaxAmountNew] = useState<number>(0);
  const [maxVirtualAmountNew, setMaxVirtualAmountNew] = useState<number>(0);
  const [maxRelocationAmountNew, setMaxRelocationAmountNew] =
    useState<number>(0);

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const toast = useRef(null);

  const partnerOptions = (() => {
    // ako kasnije vraćaš filtriranje po komercijalisti, uradi ga ovde
    if (activeUser?.role?.name === RoleType.Commercialist) {
      return partners; // ili filter(partners)
    }
    return partners;
  })();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingPartners(true);
        const unwrapArray = (resp: any): any[] => {
          const d = resp?.data;
          if (d?.result && Array.isArray(d.result)) return d.result;
          if (Array.isArray(d)) return d;
          return [];
        };

        // 1) šifarnici iz API
        const [prodResp, partnersResp, citiesResp, whResp] = await Promise.all([
          getAllProducts(),
          getAllPartners(),
          getAllCities(),
          getAllWarehouses(),
        ]);

        const pr = unwrapArray(prodResp) as Product[];
        const pa = unwrapArray(partnersResp) as Partner[];
        const ci = unwrapArray(citiesResp) as City[];
        const wh = unwrapArray(whResp) as Warehouse[];

        setProducts(pr);
        setPartners(pa);
        setCities(ci);
        setWarehouses(wh);

        // 2) dinamički podaci iz nove verzije API-ja, ograničeni na tekuću godinu
        const currentYear = new Date().getFullYear();
        const dateFrom = new Date(currentYear, 0, 1, 0, 0, 1); // 1.1. tekuće godine
        const dateTo = new Date(); // danas
        const [inputsResponse, outputsResponse, requestsResponse]: any[] =
          await Promise.all([
            // repromaterijal – prijemnice
            getAllDocuments(false, true, dateFrom, dateTo),
            // repromaterijal – otpremnice
            getAllDocuments(false, false, dateFrom, dateTo),
            // zahtevi repromaterijala
            getAllRequests(false, dateFrom, dateTo),
          ]);
        setRepromaterialInputsApi(inputsResponse?.data?.result ?? []);
        setRepromaterialOutputsApi(outputsResponse?.data?.result ?? []);
        setReproRequestsApi(requestsResponse?.data?.result ?? []);

        //stanje virtuelnog magacina
        const vwResp = await getVirtualReproWarehouse();
        const vwRow = vwResp?.data?.result;
        const json = vwRow?.repromaterialProductsJSON ?? "[]";
        let items: any[] = [];
        try {
          items = JSON.parse(json);
        } catch {
          items = [];
        }
        setVirtualStock(items);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoadingPartners(false);
      }
    };

    loadData();
  }, []);

  const makeEmptyRequest = (): Request =>
    ({
      id: "",
      type: RequestType.Import,
      product: {
        id: "",
        type: ProductType.RawMaterial,
        code: "",
        name: "",
        amount: "",
        note: "",
        category: { id: "", type: ProductType.RawMaterial, name: "" },
      } as any,
      repromaterialRequestProducts: [],
      repromaterialRequestProductsJSON: "",
      year: new Date().getFullYear(),
      partner: {} as Partner,
      place: "",
      inputCity: undefined,
      inputWarehouse: undefined,
      address: "",
      amount: 0,
      date: new Date(),
      transportCost: false,
      commercialistApproved: false,
      backofficeApproved: false,
      contact: "",
      note: "",
    }) as any;

  useEffect(() => {
    if (!show) return;
    setVisibleNewRepromaterialRequestForm(true);
    // reset newProduct
    setNewProduct({
      id: "",
      name: "",
      type: ProductType.RawMaterial,
      code: "",
      lot: "",
      amount: "",
      measuringUnit: "",
      note: "",
    });

    (async () => {
      try {
        // EDIT
        if (request?.id) {
          const resp = await getOneRequest(request.id);
          const full = resp?.data?.result as Request;

          if ((full as any)?.date)
            (full as any).date = new Date((full as any).date);

          setNewRepromaterialRequest(cloneDeep(full));
          reset();
          return;
        }

        // CREATE
        const empty = makeEmptyRequest();
        setNewRepromaterialRequest(empty);
        setProductsDataTable([]);
        reset();
      } catch (e) {
        console.error(e);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Došlo je do greške pri učitavanju zahteva"),
          life: 3000,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (newProduct?.name !== undefined) {
      if (newRepromaterialRequest.type === RequestType.Export) {
        calculateMaxAmount();
        calculateMaxAmountNew();
      }
      if (newRepromaterialRequest.type === RequestType.Relocation) {
        calculateMaxRelocationAmount();
        calculateMaxRelocationAmountNew();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    newRepromaterialRequest.inputWarehouse,
    newRepromaterialRequest.type,
    newProduct,
    repromaterialInputsApi,
    repromaterialOutputsApi,
    reproRequestsApi,
    virtualStock,
  ]);

  useEffect(() => {
    if (
      newRepromaterialRequest.repromaterialRequestProducts &&
      products.length > 0
    ) {
      //create repromaterial request datatable
      const newReproRequestProductsDataTable =
        newRepromaterialRequest.repromaterialRequestProducts?.map(
          (product: any) => ({
            id: product.id,
            name: product.name,
            amount: product.amount ? product.amount.toString() : "0",
            measuringUnit: product.measuringUnit || "",
            note: product.note || "",
          }),
        );
      setProductsDataTable(newReproRequestProductsDataTable);
    }
  }, [newRepromaterialRequest.repromaterialRequestProducts, products]);

  const showModal = () => {
    setVisibleNewRepromaterialRequestForm(
      !visibleNewRepromaterialRequestForm ? true : false,
    );
  };

  const calculateMaxAmount = () => {
    let newMaxAmount: number = 0;
    let newMaxVirtualAmount: number = 0;

    // 0) stanje u virtuelnom magacinu
    const vItem = (virtualStock ?? []).find(
      (x: any) => x.id?.toString() === newProduct.id?.toString(),
    );
    newMaxVirtualAmount = parseFloat(vItem?.amount ?? "0") || 0;

    //PRIVREMENO STOPIRANO IZUZIMANJE STANJA IZ DORADNIH CENTARA!!!

    // 1) ULAZI REPROMATERIJALA – prijemnice za sve magacine (realne + virtuelne)
    for (const repromaterialInput of repromaterialInputsApi) {
      const isFinished =
        repromaterialInput?.isDocumentFinished === true ||
        repromaterialInput?.finishedDocument === true;

      // const isFinishingCenterWarehouse =
      //   repromaterialInput?.isWarehouseFinishingCenter === true;

      // const sameWarehouseForRelocation =
      //   newRepromaterialRequest.type === RequestType.Relocation
      //     ? repromaterialInput?.warehouseName ===
      //       newRepromaterialRequest.inputWarehouse?.name
      //     : true;

      if (
        !isFinished //||
        // isFinishingCenterWarehouse || //ulazi u doradni centar se izuzimaju iz obracuna stanja
        // !sameWarehouseForRelocation
      )
        continue;

      const productIds = (repromaterialInput?.reproProducts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      const amountsRaw = (repromaterialInput?.reproAmounts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      for (let idx = 0; idx < productIds.length; idx++) {
        const prodId = productIds[idx];

        if (!newProduct?.id || prodId.toString() !== newProduct.id.toString()) {
          continue;
        }

        const raw = amountsRaw[idx] ?? "0";
        const amount = parseFloat(raw) || 0; // npr. "10kg" -> 10

        console.log("ulaz", raw);
        if (repromaterialInput?.isWarehouseVirtual === true) {
          // zadržavamo postojeće ponašanje – zadnja virtuelna prijemnica overwrituje stanje
          continue;
        } else {
          newMaxAmount += amount;
        }
      }
    }
    // 2) IZLAZI REPROMATERIJALA – direktne otpremnice (bez dispozicija)
    //PRIVREMENO STOPIRANO IZUZIMANJE STANJA IZ DORADNIH CENTARA!!!

    for (const repromaterialOutput of repromaterialOutputsApi) {
      const isFinished =
        repromaterialOutput?.isDocumentFinished === true ||
        repromaterialOutput?.finishedDocument === true;

      // const isFinishingCenterWarehouse =
      //   repromaterialOutput?.isWarehouseFinishingCenter === true;

      const hasDisposition =
        repromaterialOutput?.dispositionId !== undefined &&
        repromaterialOutput?.dispositionId !== null &&
        repromaterialOutput?.dispositionId !== "";

      // const sameWarehouseForRelocation =
      //   newRepromaterialRequest.type === RequestType.Relocation
      //     ? repromaterialOutput?.warehouseName ===
      //       newRepromaterialRequest.inputWarehouse?.name
      //     : true;

      if (
        !isFinished ||
        //isFinishingCenterWarehouse ||
        hasDisposition //||
        //!sameWarehouseForRelocation
      )
        continue;

      const productIds = (repromaterialOutput?.reproProducts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      const amountsRaw = (repromaterialOutput?.reproAmounts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      for (let idx = 0; idx < productIds.length; idx++) {
        const prodId = productIds[idx];

        if (!newProduct?.id || prodId.toString() !== newProduct.id.toString()) {
          continue;
        }

        const raw = amountsRaw[idx] ?? "0";
        const amount = parseFloat(raw) || 0;
        console.log("direkt.izlaz", raw);
        newMaxAmount -= amount;
      }
    }
    // 3) ODUZIMANJE NA OSNOVU ZAHTEVA (prodaja + premeštaj)

    //PRIVREMENO STOPIRANO IZUZIMANJE STANJA IZ DORADNIH CENTARA!!!

    // for (const reproRequest of reproRequestsApi) {
    //   const isFinishingCenterWarehouse =
    //     reproRequest?.isWarehouseFinishingCenter === true;

    //   // U globalnom dostupnom za prodaju:
    //   // - Export zahtev uvek umanjuje stanje
    //   // - Relocation iz regularnog magacina umanjuje stanje
    //   // - Relocation iz doradnog centra NE utiče ovde,
    //   //   jer prijemnica u ciljni regularni magacin već povećava stanje,
    //   //   a dodatni + bi duplirao, a - bi ga poništio
    //   if (
    //     reproRequest.type === RequestType.Relocation &&
    //     isFinishingCenterWarehouse
    //   ) {
    //     continue;
    //   }
    //   if (
    //     reproRequest.type !== RequestType.Export &&
    //     reproRequest.type !== RequestType.Relocation
    //   ) {
    //     continue;
    //   }

    //   // const isFinishingCenterWarehouse =
    //   //   reproRequest?.isWarehouseFinishingCenter === true;

    //   // pravila:
    //   // - Export iz svih magacina osim doradnog -> umanjuje stanje
    //   // - Export iz doradnog centra -> ne utiče
    //   // - Import iz doradnog centra -> ne utiče
    //   // - Relocation -> umanjuje stanje
    //   // = SUM(ulazi – završene prijemnice, svi realni magacini)
    //   // - SUM(direktni izlazi – završene otpremnice BEZ dispositionId)
    //   // - SUM(zahtevi prodaje – Export)
    //   // - SUM(zahtevi premeštaja – Relocation)
    //   // Export -> minus
    //   // Relocation + isWarehouseFinishingCenter === false -> minus
    //   // Relocation + isWarehouseFinishingCenter === true -> 0

    //   // if (reproRequest.type === RequestType.Export) {
    //   //   if (isFinishingCenterWarehouse) {
    //   //     continue;
    //   //   }
    //   // } else if (reproRequest.type === RequestType.Relocation) {
    //   //   if (!isFinishingCenterWarehouse) {
    //   //     continue;
    //   //   }
    //   // } else {
    //   //   continue;
    //   // }

    //   const productIds = (reproRequest?.reproProducts ?? "")
    //     .split(";")
    //     .map((x: string) => x.trim())
    //     .filter((x: string) => x !== "");

    //   const amountsRaw = (reproRequest?.reproAmounts ?? "")
    //     .split(";")
    //     .map((x: string) => x.trim())
    //     .filter((x: string) => x !== "");

    //   for (let idx = 0; idx < productIds.length; idx++) {
    //     const prodId = productIds[idx];

    //     if (!newProduct?.id || prodId.toString() !== newProduct.id.toString()) {
    //       continue;
    //     }

    //     const raw = amountsRaw[idx] ?? "0";
    //     const amount = parseFloat(raw) || 0;

    //     //if (reproRequest.type === RequestType.Export) {
    //     console.log("zahtev", raw);
    //     newMaxAmount -= amount;
    //     //}

    //     // if (reproRequest.type === RequestType.Relocation) {
    //     //   console.log("zahtev premeštaja iz doradnog", raw);
    //     //   newMaxAmount += amount;
    //     // }
    //   }
    // }

    //VRACEN STARI NACIN OBRACUNA
    // 3) ODUZIMANJE NA OSNOVU ZAHTEVA (prodaja + premeštaj)
    for (const reproRequest of reproRequestsApi) {
      if (
        reproRequest.type !== RequestType.Export &&
        reproRequest.type !== RequestType.Relocation
      ) {
        continue;
      }

      const productIds = (reproRequest?.reproProducts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      const amountsRaw = (reproRequest?.reproAmounts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      for (let idx = 0; idx < productIds.length; idx++) {
        const prodId = productIds[idx];

        if (!newProduct?.id || prodId.toString() !== newProduct.id.toString()) {
          continue;
        }

        const raw = amountsRaw[idx] ?? "0";
        const amount = parseFloat(raw) || 0;

        console.log("zahtevi", reproRequest.type, raw);
        newMaxAmount -= amount;
      }
    }

    console.log("ukupno", newMaxAmount);
    if (newMaxAmount < 0) {
      //ako nema dovoljne kolicine u realnim magacinima, umanjiti kolicinu iz virtuelnog
      newMaxVirtualAmount += newMaxAmount;
      if (newMaxVirtualAmount < 0) {
        newMaxVirtualAmount = 0;
      }
      newMaxAmount = 0;
    }
    setMaxAmount(parseFloat(newMaxAmount.toFixed(3)));
    setMaxVirtualAmount(newMaxVirtualAmount);
  };

  const calculateMaxRelocationAmount = () => {
    let newMaxAmount: number = 0;
    // 1) ULAZI – samo realni magacini
    for (const repromaterialInput of repromaterialInputsApi) {
      const isFinished =
        repromaterialInput?.isDocumentFinished === true ||
        repromaterialInput?.finishedDocument === true;

      const sameWarehouseForRelocation =
        newRepromaterialRequest.type === RequestType.Relocation
          ? repromaterialInput?.warehouseName ===
            newRepromaterialRequest.inputWarehouse?.name
          : true;

      if (!isFinished || !sameWarehouseForRelocation) continue;

      if (repromaterialInput?.isWarehouseVirtual === true) {
        // za premeštaj nas interesuju samo realni magacini
        continue;
      }

      const productIds = (repromaterialInput?.reproProducts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      const amountsRaw = (repromaterialInput?.reproAmounts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      for (let idx = 0; idx < productIds.length; idx++) {
        const prodId = productIds[idx];

        if (!newProduct?.id || prodId.toString() !== newProduct.id.toString()) {
          continue;
        }

        const raw = amountsRaw[idx] ?? "0";
        const amount = parseFloat(raw) || 0;

        newMaxAmount += amount;
      }
    }

    // 2) IZLAZI – sve otpremnice (za izabrani magacin kod premeštaja)
    for (const repromaterialOutput of repromaterialOutputsApi) {
      const isFinished =
        repromaterialOutput?.isDocumentFinished === true ||
        repromaterialOutput?.finishedDocument === true;

      const sameWarehouseForRelocation =
        newRepromaterialRequest.type === RequestType.Relocation
          ? repromaterialOutput?.warehouseName ===
            newRepromaterialRequest.inputWarehouse?.name
          : true;

      if (!isFinished || !sameWarehouseForRelocation) continue;

      const productIds = (repromaterialOutput?.reproProducts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      const amountsRaw = (repromaterialOutput?.reproAmounts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      for (let idx = 0; idx < productIds.length; idx++) {
        const prodId = productIds[idx];

        if (!newProduct?.id || prodId.toString() !== newProduct.id.toString()) {
          continue;
        }

        const raw = amountsRaw[idx] ?? "0";
        const amount = parseFloat(raw) || 0;

        newMaxAmount -= amount;
      }
    }
    setMaxAmount(parseFloat(newMaxAmount.toFixed(3)));
  };

  const calculateMaxAmountNew = async () => {
    try {
      if (!newProduct?.id) return;

      const currentYear = new Date().getFullYear(); // isto kao postojeći FE (za sad)
      const resp = await getReproAvailableSales({
        year: currentYear,
        productId: newProduct.id,
      });

      const withoutVirtual = Number(
        resp?.data?.result?.availableWithoutVirtual ?? 0,
      );
      const withVirtual = Number(resp?.data?.result?.availableWithVirtual ?? 0);

      const virtualPart = Math.max(0, withVirtual - withoutVirtual);

      setMaxAmountNew(Number(withoutVirtual.toFixed(3)));
      setMaxVirtualAmountNew(Number(virtualPart.toFixed(3)));

      console.log(
        "[STOCK_BALANCE][REPRO][EXPORT] old(real,virt) =",
        maxAmount,
        maxVirtualAmount,
        " new(real,virt) =",
        withoutVirtual,
        virtualPart,
        " raw(without,with) =",
        withoutVirtual,
        withVirtual,
      );
    } catch (e) {
      console.log("[STOCK_BALANCE][REPRO][EXPORT] ERROR", e);
    }
  };

  const calculateMaxRelocationAmountNew = async () => {
    try {
      if (!newProduct?.id) return;
      if (!newRepromaterialRequest?.inputWarehouse?.id) return;

      const currentYear = new Date().getFullYear(); // isto kao postojeći FE (za sad)
      const resp = await getOnHand({
        year: currentYear,
        warehouseId: newRepromaterialRequest.inputWarehouse.id,
        productId: newProduct.id,
      });

      const onHand = Number(resp?.data?.result?.onHand ?? 0);
      setMaxRelocationAmountNew(Number(onHand.toFixed(3)));

      console.log(
        "[STOCK_BALANCE][REPRO][RELOCATION] old(maxAmount) =",
        maxAmount,
        " new(onHand) =",
        onHand,
      );
    } catch (e) {
      console.log("[STOCK_BALANCE][REPRO][RELOCATION] ERROR", e);
    }
  };
  console.log(maxAmountNew, maxVirtualAmountNew, maxRelocationAmountNew);
  const handleNewRepromaterialRequestForm = (prop: string, value: any) => {
    setNewRepromaterialRequest((newRepromaterialRequest) => ({
      ...newRepromaterialRequest,
      [prop]: value,
    }));
  };

  const handleRequestTypeChange = (prop: string, value: any) => {
    let copyOfNewRepromaterialRequest = cloneDeep(newRepromaterialRequest);
    copyOfNewRepromaterialRequest.type = value;
    if (value === RequestType.Import) {
      copyOfNewRepromaterialRequest.partner = {} as Partner;
    }
    if (value === RequestType.Relocation) {
      copyOfNewRepromaterialRequest.partner = partners.find(
        (item) => item.name == "RWA Srbija doo",
      );
    }
    setNewRepromaterialRequest(copyOfNewRepromaterialRequest);
  };

  const handlePartnerChange = (prop: string, value: any) => {
    let copyOfNewRepromaterialRequest = cloneDeep(newRepromaterialRequest);
    const selectedPartner = partners.find((item) => item.id == value);
    copyOfNewRepromaterialRequest.partner = selectedPartner;
    if (newRepromaterialRequest.type === RequestType.Export) {
      copyOfNewRepromaterialRequest.address = selectedPartner?.address;
      copyOfNewRepromaterialRequest.inputCity = cities.find(
        (item) => item.id == selectedPartner?.city.id,
      );
      copyOfNewRepromaterialRequest.inputWarehouse = undefined;
      copyOfNewRepromaterialRequest.place = cities.find(
        (item) => item.id == selectedPartner?.city.id,
      )?.name;
    }
    setNewRepromaterialRequest(copyOfNewRepromaterialRequest);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewRepromaterialRequest = cloneDeep(newRepromaterialRequest);
    copyOfNewRepromaterialRequest.inputWarehouse = warehouses.find(
      (item) => item.id == value,
    );
    copyOfNewRepromaterialRequest.inputCity = undefined;
    copyOfNewRepromaterialRequest.place = warehouses.find(
      (item) => item.id == value,
    )?.name;
    setNewRepromaterialRequest(copyOfNewRepromaterialRequest);
  };

  const handleCityChange = (prop: string, value: any) => {
    let copyOfNewRepromaterialRequest = cloneDeep(newRepromaterialRequest);
    copyOfNewRepromaterialRequest.inputCity = cities.find(
      (item) => item.id == value,
    );
    copyOfNewRepromaterialRequest.inputWarehouse = undefined;
    copyOfNewRepromaterialRequest.place = cities.find(
      (item) => item.id == value,
    )?.name;
    setNewRepromaterialRequest(copyOfNewRepromaterialRequest);
  };

  const handleNewCity = (prop: string, value: string) => {
    prop === "newCityName"
      ? setNewCity((newCity) => ({ ...newCity, name: value }))
      : setNewCity((newCity) => ({ ...newCity, code: value }));
  };

  const addNewCity = () => {
    const save = async () => {
      try {
        if (newCity.id) {
          await updateOneCity(newCity.id, {
            name: newCity.name,
            code: newCity.code,
          });
        } else {
          const code = new Date().getTime().toFixed();
          await createCity({ name: newCity.name, code });
        }

        const citiesResp = await getAllCities();
        setCities(citiesResp?.data?.result ?? []);
      } catch (e) {
        console.log("add/update city failed", e);
      } finally {
        setVisibleNewCityAddForm(false);
        setNewCity({ id: "", code: "", name: "" });
      }
    };
    save();
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(newProduct);
    copyOfNewProduct.id = value;
    copyOfNewProduct.name = value;
    copyOfNewProduct.measuringUnit = products.find(
      (product) => product.id === value,
    )?.measuringUnit;
    setNewProduct(copyOfNewProduct);
  };

  const handleNewProduct = (prop: string, value: any) => {
    setNewProduct((newProduct) => ({
      ...newProduct,
      [prop]: value,
    }));
  };

  const addNewProduct = () => {
    // Proveri da li je ovo ažuriranje postojećeg proizvoda
    const existingProduct =
      newRepromaterialRequest.repromaterialRequestProducts?.find(
        (item) => item.id == newProduct.id,
      );
    const isEditingExistingProduct = !!existingProduct;
    const oldAmount = existingProduct
      ? parseFloat(existingProduct.amount as string)
      : 0;
    const newAmount = parseFloat(newProduct.amount as string) ?? 0;

    // Ako se količina smanjuje (edit postojećeg proizvoda sa manjom količinom), ne proveravaj max amount
    const isDecreasingAmount =
      isEditingExistingProduct && newAmount < oldAmount;
    // Ako se količina povećava, dozvoli do max dostupno + trenutna količina u zahtevu
    const maxAllowedAmount = isEditingExistingProduct
      ? maxAmount + maxVirtualAmount + oldAmount // Dozvoli povećanje do max + trenutna
      : maxAmount + maxVirtualAmount; // Za novi proizvod, samo max

    if (
      (newRepromaterialRequest.type === RequestType.Relocation ||
        newRepromaterialRequest.type === RequestType.Export) &&
      !isDecreasingAmount && // NE proveravaj max amount ako smanjujemo količinu
      maxAllowedAmount < newAmount
    ) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        summary: t(`Neodgovarajuća količina`),
        detail: isEditingExistingProduct
          ? t(
              `Maksimalno dozvoljena količina za povećanje je ${maxAllowedAmount}. Trenutno u zahtevu: ${oldAmount}, dostupno na stanju: ${
                maxAmount + maxVirtualAmount
              }`,
            )
          : t(
              `Uneta količina proizvoda veća je od maksimalno dostupne količine. Maksimalno dostupna količina za izabrani proizvod je ${maxAmount}. Količina za izabrani proizvod u virtuelnom magacinu je ${maxVirtualAmount}`,
            ),
        sticky: true,
      });
      return;
    }

    if (newProduct.amount === "") {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Nije uneta količina proizvoda`),
        life: 3000,
      });
      return;
    }

    if ((newProduct.amount as string).indexOf(",") >= 0) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(
          `Neispravan unos količine. Decimalni broj treba uneti sa . a ne sa ,`,
        ),
        life: 3000,
      });
      return;
    }

    if (!existingProduct) {
      //add product to allProductList
      let copyOfNewRepromaterialRequest = cloneDeep(newRepromaterialRequest);
      let copyOfNewProduct = cloneDeep(newProduct);
      copyOfNewRepromaterialRequest.product = cloneDeep(newProduct);
      copyOfNewRepromaterialRequest.repromaterialRequestProducts?.push(
        copyOfNewProduct,
      );
      setNewRepromaterialRequest(copyOfNewRepromaterialRequest);
    } else {
      //update existing product
      let copyOfNewRepromaterialRequest = cloneDeep(newRepromaterialRequest);
      newRepromaterialRequest.repromaterialRequestProducts?.map(
        (product, index) => {
          if (product.id === newProduct.id)
            //@ts-ignore
            copyOfNewRepromaterialRequest.repromaterialRequestProducts[index] =
              cloneDeep(newProduct);
        },
      );
      setNewRepromaterialRequest(copyOfNewRepromaterialRequest);
    }

    //reset newProductFields
    setNewProduct({
      id: "",
      name: "",
      type: ProductType.RawMaterial,
      code: "",
      lot: "",
      amount: "",
      measuringUnit: "",
      note: "",
    });
  };

  const buildPayload = () => {
    const payload: any = cloneDeep(newRepromaterialRequest);

    // datum validan
    const d = payload?.date ? new Date(payload.date) : null;
    if (!d || isNaN(d.getTime())) payload.date = new Date();

    // proizvodi -> JSON (backend + excel)
    const items = (payload.repromaterialRequestProducts ?? []).map(
      (p: any) => ({
        id: p.id,
        amount: p.amount ?? "",
        note: p.note ?? "",
        measuringUnit: p.measuringUnit ?? "",
        name: p.name ?? "",
      }),
    );
    payload.repromaterialRequestProductsJSON = JSON.stringify(items);

    const ids = items.map((x: any) => String(x.id)).filter(Boolean);
    const amounts = items
      .map((x: any) => String(x.amount ?? ""))
      .filter(Boolean);
    const notes = items.map((x: any) => String(x.note ?? ""));

    payload.reproProducts = ids.length ? ids.join(";") + ";" : "";
    payload.reproAmounts = amounts.length ? amounts.join(";") + ";" : "";
    payload.reproNotes = notes.length ? notes.join(";") + ";" : "";

    return payload;
  };

  const closeModal = () => setVisibleNewRepromaterialRequestForm(false);

  const resetToEmpty = () => {
    reset();
    setNewRepromaterialRequest(makeEmptyRequest());
    setNewProduct({
      id: "",
      name: "",
      type: ProductType.RawMaterial,
      code: "",
      lot: "",
      amount: "",
      measuringUnit: "",
      note: "",
    });
    setProductsDataTable([]);
    setVisibleNewCityAddForm(false);
    setNewCity({ id: "", code: "", name: "" });
  };

  const handleResetModal = () => {
    resetToEmpty();
    closeModal();
  };

  const refreshReproRequestsApi = async () => {
    const currentYear = new Date().getFullYear();
    const dateFrom = new Date(currentYear, 0, 1, 0, 0, 1);
    const dateTo = new Date();

    const requestsResponse = await getAllRequests(false, dateFrom, dateTo);
    setReproRequestsApi(requestsResponse?.data?.result ?? []);
  };

  const onSubmit = async () => {
    if (isSaving) return;

    //da bi se kreirao zahtev potrebno je da postoji bar jedan proizvod u listi repromaterijal za koji se kreira zahtev
    if (newRepromaterialRequest.repromaterialRequestProducts?.length === 0) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(
          `Neophodno je da postoji bar jedan proizvod u listi repromaterijala za koji se kreira zahtev`,
        ),
        life: 3000,
      });
      return;
    }

    setIsSaving(true);

    try {
      const payload = buildPayload();
      if (payload.id) {
        await updateOneRequest(payload.id, payload);
      } else {
        // KLJUČNO: kod kreiranja izbaci id
        delete payload.id;
        await addNewRequest(payload);
      }

      handleResetModal();
      setProductsDataTable([]);
      refreshingData?.();

      refreshReproRequestsApi().catch(console.error);

      // @ts-ignore
      toast.current?.show({
        severity: "success",
        detail: t("Uspešno sačuvano"),
        life: 3000,
      });
    } catch (e) {
      console.error(e);
      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail: t("Došlo je do greške pri snimanju zahteva"),
        life: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const isRepromaterial = (product: Product) => {
    return product.type === ProductType.RawMaterial;
  };

  const handleCommercialistApprove = () => {
    const copyOfNewRepromaterialRequest: Request = cloneDeep(
      newRepromaterialRequest,
    );
    copyOfNewRepromaterialRequest.commercialistApproved =
      !newRepromaterialRequest.commercialistApproved;
    setNewRepromaterialRequest(copyOfNewRepromaterialRequest);
  };

  const handleBackofficeApprove = () => {
    const copyOfNewRepromaterialRequest: Request = cloneDeep(
      newRepromaterialRequest,
    );
    copyOfNewRepromaterialRequest.backofficeApproved =
      !newRepromaterialRequest.backofficeApproved;
    setNewRepromaterialRequest(copyOfNewRepromaterialRequest);
  };

  let addButtonStyle: React.CSSProperties = {};
  if (buttonStyle) {
    addButtonStyle = buttonStyle;
  } else {
    addButtonStyle.background = "rgba(0, 165, 83, 1)";
    addButtonStyle.border = "none";
  }

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  let addButtonNewCityStyle: React.CSSProperties = {};
  if (buttonNewCityStyle) {
    addButtonNewCityStyle = buttonNewCityStyle;
  } else {
    addButtonNewCityStyle.background = "rgba(21, 115, 71, 1)";
    addButtonNewCityStyle.border = "none";
  }

  const getHeaderClass = () => {
    switch (newRepromaterialRequest.type) {
      case RequestType.Import:
        return "p-dialog-header p-dialog-header-import";
      case RequestType.Export:
        return "p-dialog-header p-dialog-header-export";
      case RequestType.Relocation:
        return "p-dialog-header p-dialog-header-relocation";
      default:
        return "p-dialog-header";
    }
  };

  const header = (
    <div
      className={`p-dialog-header ${getHeaderClass()}`}
      style={{ borderBottom: "none" }}
    >
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-truck" style={{ fontSize: 18 }}></i>
      </span>
      {newRepromaterialRequest.id === "" ? (
        <span>{t(`Kreiranje zahteva repromaterijala`)}</span>
      ) : (
        <span>
          {t("Ažuriranje zahteva repromaterijala ")}
          {`${newRepromaterialRequest?.requestNumberFormatted ?? ""}`}
        </span>
      )}
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={isSaving ? t("Snimanje...") : t("Kreiraj")}
        onButtonClick={handleSubmit(onSubmit, onError)}
        addStyle={addButtonSaveStyle}
        disabled={isSaving || activeUser?.role?.name === RoleType.Director}
      />
      <ButtonInput
        labelText={t("Resetuj")}
        onButtonClick={handleResetModal}
        addStyle={addButtonResetStyle}
        disabled={activeUser?.role?.name === RoleType.Director}
      />
    </div>
  );

  const nameOfProduct = (rowData: any) => {
    return products.find((item) => item.id == rowData.name)?.name as string;
  };

  const measuringUnitOfProduct = (rowData: any) => {
    return products.find((item) => item.id == rowData.name)
      ?.measuringUnit as string;
  };

  const editActionBodyTemplate = (rowData: any) => {
    return (
      <ButtonInput
        labelText=""
        icon="bi bi-pencil"
        onButtonClick={() => {
          setNewProduct({
            id: rowData.id,
            name: rowData.name,
            type: ProductType.RawMaterial,
            code: "",
            lot: "",
            amount: rowData.amount,
            measuringUnit: rowData?.measuringUnit ?? "",
            note: rowData.note,
          });
        }}
        addStyle={{
          backgroundColor: "white",
          color: "rgba(21, 115, 71, 1)",
          border: "none",
        }}
      />
    );
  };

  const deleteActionBodyTemplate = (rowData: any) => {
    return (
      <ButtonInput
        labelText=""
        icon="bi bi-trash"
        onButtonClick={() => {
          const productToDelete =
            newRepromaterialRequest.repromaterialRequestProducts?.find(
              (item) => item.id == rowData.id,
            );
          let copyOfNewRepromaterialRequest = cloneDeep(
            newRepromaterialRequest,
          );
          copyOfNewRepromaterialRequest.repromaterialRequestProducts = [];
          newRepromaterialRequest.repromaterialRequestProducts?.map(
            (product) => {
              if (product.id !== productToDelete?.id)
                copyOfNewRepromaterialRequest.repromaterialRequestProducts?.push(
                  product,
                );
            },
          );
          setNewRepromaterialRequest(copyOfNewRepromaterialRequest);
          setNewProduct({
            id: "",
            name: "",
            type: ProductType.RawMaterial,
            code: "",
            lot: "",
            amount: "",
            measuringUnit: "",
            note: "",
          });
        }}
        addStyle={{
          backgroundColor: "white",
          color: "rgba(21, 115, 71, 1)",
          border: "none",
        }}
      />
    );
  };

  return (
    <>
      <ButtonInput
        labelText={buttonLabel as string}
        icon={buttonIcon}
        onButtonClick={showModal}
        addStyle={addButtonStyle}
      />
      <Toast ref={toast} position={"top-right"} />
      {visibleNewRepromaterialRequestForm ? (
        <div>
          <Dialog
            maximizable
            maximized={isMobile}
            header={header}
            headerClassName={getHeaderClass()}
            visible={true}
            style={{ width: "50vw" }}
            className="new-repromaterial-dialog"
            footer={footer}
            blockScroll={false}
            draggable={false}
            onHide={() => {
              closeModal();
              resetToEmpty();
            }}
          >
            <div className="new-repromaterial-request-form-body">
              <DropdownInput
                id="type"
                value={newRepromaterialRequest.type as string}
                onValueChange={handleRequestTypeChange}
                options={[
                  { label: t("Prijem"), value: RequestType.Import },
                  { label: t("Prodaja"), value: RequestType.Export },
                  { label: t("Premeštaj"), value: RequestType.Relocation },
                ]}
                labelText={`${t("Tip zahteva")} *`}
                placeholderText={`${t("Izaberite iz liste")}`}
                control={control}
                setValue={setValue}
                errors={errors}
                colorType={newRepromaterialRequest.type as string}
              />
              {(newRepromaterialRequest.type === RequestType.Import ||
                newRepromaterialRequest.type === RequestType.Export) && (
                <div className="city-content">
                  <div className="city-id-content">
                    {newRepromaterialRequest.type === RequestType.Import && (
                      <DropdownInput
                        id="inputCity"
                        value={newRepromaterialRequest.inputCity?.id}
                        onValueChange={handleCityChange}
                        optionValue="id"
                        optionLabel="name"
                        options={cities}
                        filter={true}
                        labelText={`${t("Polazno mesto")} *`}
                        placeholderText={`${t("Izaberite iz liste")}`}
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        rules={{ required: true }}
                        colorType={newRepromaterialRequest.type as string}
                      />
                    )}
                    {newRepromaterialRequest.type === RequestType.Export && (
                      <DropdownInput
                        id="inputCity"
                        value={newRepromaterialRequest.inputCity?.id}
                        onValueChange={handleCityChange}
                        optionValue="id"
                        optionLabel="name"
                        options={cities}
                        filter={true}
                        labelText={`${t("Prijemno mesto")} *`}
                        placeholderText={`${t("Izaberite iz liste")}`}
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        rules={{ required: true }}
                        colorType={newRepromaterialRequest.type as string}
                      />
                    )}
                  </div>
                  <div className="city-add-button">
                    <ButtonInput
                      labelText={t("Dodaj mesto")}
                      onButtonClick={() => {
                        setVisibleNewCityAddForm(
                          !visibleNewCityAddForm ? true : false,
                        );
                      }}
                      icon="bi bi-plus-square"
                      iconAlign="left"
                      addStyle={addButtonNewCityStyle}
                      disabled={activeUser?.role?.name === RoleType.Director}
                    />
                  </div>
                  {visibleNewCityAddForm && (
                    <div className="new-city-name">
                      <TextInputWithIcon
                        id="newCityName"
                        value={newCity.name}
                        onValueChange={handleNewCity}
                        labelText={t("Naziv mesta/naselja")}
                        icon="bi bi-signpost"
                        iconAlign="left"
                        placeholderText={t("Unesite mesto/naselje")}
                        control={control}
                        errors={errors}
                        colorType={newRepromaterialRequest.type as string}
                      />
                    </div>
                  )}
                  {visibleNewCityAddForm && (
                    <div className="new-city-add-button">
                      <ButtonInput
                        labelText={t("Dodaj")}
                        onButtonClick={addNewCity}
                        addStyle={addButtonNewCityStyle}
                      />
                    </div>
                  )}
                </div>
              )}
              {newRepromaterialRequest.type === RequestType.Export && (
                <TextInput
                  id="address"
                  value={newRepromaterialRequest.address as string}
                  onValueChange={handleNewRepromaterialRequestForm}
                  labelText={`${t("Adresa prijemnog mesta")} *`}
                  placeholderText={`${t("Unesite adresu prijemnog mesta")}`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{ required: true }}
                  colorType={newRepromaterialRequest.type as string}
                />
              )}
              {newRepromaterialRequest.type === RequestType.Export && (
                <div className="date-cost-included-content">
                  <div className="is-cost-included">
                    <CheckboxInput
                      id="commercialistApproved"
                      checked={
                        newRepromaterialRequest.commercialistApproved as boolean
                      }
                      onValueChange={handleCommercialistApprove}
                      labelText={t("Komercijalista odobrio")}
                      control={control}
                      errors={errors}
                      colorType={newRepromaterialRequest.type as string}
                      disabled={
                        !(
                          activeUser?.role?.name === RoleType.Commercialist ||
                          activeUser?.role?.name === RoleType.CommercialistII ||
                          activeUser?.role?.name === RoleType.Logistician ||
                          activeUser?.role?.name === RoleType.Administrator ||
                          activeUser?.role?.name === RoleType.Superadmin
                        )
                      }
                    />
                  </div>
                  <div className="is-cost-included">
                    <CheckboxInput
                      id="backofficeApproved"
                      checked={
                        newRepromaterialRequest.backofficeApproved as boolean
                      }
                      onValueChange={handleBackofficeApprove}
                      labelText={t("Egzekucija odobrila")}
                      control={control}
                      errors={errors}
                      colorType={newRepromaterialRequest.type as string}
                      disabled={
                        !(
                          activeUser?.role?.name === RoleType.Backoffice ||
                          activeUser?.role?.name === RoleType.Logistician ||
                          activeUser?.role?.name === RoleType.Administrator ||
                          activeUser?.role?.name === RoleType.Superadmin
                        )
                      }
                    />
                  </div>
                </div>
              )}
              {newRepromaterialRequest.type === RequestType.Relocation && (
                <DropdownInput
                  id="inputWarehouse"
                  value={newRepromaterialRequest.inputWarehouse?.id}
                  onValueChange={handleWarehouseChange}
                  optionValue="id"
                  optionLabel="name"
                  options={warehouses.filter((item) => item.virtual !== true)}
                  labelText={`${t("Izlazno mesto")} *`}
                  placeholderText={`${t("Izaberite iz liste")}`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{ required: true }}
                  colorType={newRepromaterialRequest.type as string}
                />
              )}
              <DropdownInput
                id="partner"
                value={newRepromaterialRequest.partner?.id}
                onValueChange={handlePartnerChange}
                optionValue="id"
                optionLabel="name"
                options={isLoadingPartners ? [] : partnerOptions}
                filter={true}
                labelText={
                  newRepromaterialRequest.type === RequestType.Export
                    ? `${t("Kupac robe")} *`
                    : newRepromaterialRequest.type === RequestType.Import
                      ? `${t("Dobavljač robe")} *`
                      : `${t("Vlasnik robe")} *`
                }
                placeholderText={
                  isLoadingPartners
                    ? `${t("Učitavanje partnera...")}`
                    : `${t("Izaberite iz liste")}`
                }
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                colorType={newRepromaterialRequest.type as string}
                disabled={isLoadingPartners}
              />
              <div className="date-cost-included-content">
                <div className="date">
                  <DateInput
                    id="date"
                    value={
                      newRepromaterialRequest.date
                        ? new Date(newRepromaterialRequest.date)
                        : null
                    }
                    onValueChange={handleNewRepromaterialRequestForm}
                    labelText={`${t("Izaberite datum")} *`}
                    placeholderText={`${t("Datum")}`}
                    icon="bi bi-calendar-check"
                    minDate={new Date()}
                    control={control}
                    errors={errors}
                    colorType={newRepromaterialRequest.type as string}
                  />
                </div>
                <div className="is-cost-included">
                  <RadioButtonInput
                    id="transportCost"
                    value={newRepromaterialRequest.transportCost as boolean}
                    radioButtonValue1={`${t("DA")}`}
                    radioButtonValue2={`${t("NE")}`}
                    onValueChange={handleNewRepromaterialRequestForm}
                    labelText={t(`Trošak prevoza RWA`)}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    colorType={newRepromaterialRequest.type as string}
                  />
                </div>
              </div>
              <TextInput
                id="contact"
                value={newRepromaterialRequest.contact ?? ""}
                onValueChange={handleNewRepromaterialRequestForm}
                labelText={`${t("Kontakt")}`}
                placeholderText={`${t("Unesite kontakt")}`}
                control={control}
                setValue={setValue}
                errors={errors}
                colorType={newRepromaterialRequest.type as string}
              />
              <div></div>
              <DropdownInput
                id="product"
                value={newProduct.id}
                onValueChange={handleProductChange}
                optionValue="id"
                optionLabel="name"
                options={products.filter(isRepromaterial)}
                filter={true}
                labelText={`${t("Roba")} *`}
                placeholderText={`${t("Izaberite iz liste")}`}
                control={control}
                setValue={setValue}
                errors={errors}
                //rules={{ required: true }}
                colorType={newRepromaterialRequest.type as string}
              />
              <div className="new-product">
                <div className="product-amount">
                  <TextInput
                    id="amount"
                    value={newProduct.amount as string}
                    onValueChange={handleNewProduct}
                    labelText={`
                      ${t("Količina")}
                      ${
                        newRepromaterialRequest.type === RequestType.Export
                          ? `(lager: ${maxAmount}, virtual: ${maxVirtualAmount})`
                          : newRepromaterialRequest.type ===
                              RequestType.Relocation
                            ? `(maksimalno: ${maxAmount})`
                            : ""
                      }*
                    `}
                    placeholderText={`${t("Unesite količinu robe")}`}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    rules={{
                      //required: true,
                      pattern: {
                        value: /^\d*\.?\d*$/,
                        message: "Please enter a number",
                      },
                    }}
                    colorType={newRepromaterialRequest.type as string}
                  />
                </div>
                <div className="add-product">
                  <ButtonInput
                    labelText={`${t("Dodaj robu")}`}
                    icon="bi bi-plus-square"
                    onButtonClick={addNewProduct}
                    addStyle={{
                      border: "none",
                      background: "rgba(21, 115, 71, 1)",
                      color: "#ffffff",
                      height: "2.6rem",
                    }}
                    disabled={activeUser?.role?.name === RoleType.Director}
                  />
                </div>
              </div>
              <TextInput
                id="note"
                value={newProduct.note as string}
                onValueChange={handleNewProduct}
                labelText={`${t("Napomena za pojedinačnu robu")}`}
                placeholderText={`${t("Unesite napomenu za pojedinačnu robu")}`}
                control={control}
                setValue={setValue}
                errors={errors}
                colorType={newRepromaterialRequest.type as string}
              />
              <div className="data-table-container">
                <DataTable value={productsDataTable}>
                  <Column
                    body={nameOfProduct}
                    header={`${t("Naziv proizvoda")}:`}
                  ></Column>
                  <Column field="amount" header={`${t("Količina")}:`}></Column>
                  <Column
                    body={measuringUnitOfProduct}
                    header={`${t("Merna jedinica")}:`}
                  ></Column>
                  <Column field="note" header={`${t("Napomena")}:`}></Column>
                  <Column
                    body={editActionBodyTemplate}
                    className="datatable-button-column"
                    header={`${t("Izmeni:")}`}
                  />
                  <Column
                    body={deleteActionBodyTemplate}
                    className="datatable-button-column"
                    header={`${t("Izbriši:")}`}
                  />
                </DataTable>
              </div>
              <div className="note-field">
                <TextAreaInput
                  id="note"
                  value={newRepromaterialRequest.note as string}
                  onValueChange={handleNewRepromaterialRequestForm}
                  rows={1}
                  cols={50}
                  labelText={`${t("Napomena")}`}
                  placeholderText={`${t("Unesite napomenu")}`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  colorType={newRepromaterialRequest.type as string}
                />
              </div>
            </div>
          </Dialog>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
